// libs
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// apis
import {
  getProductDetailData,
  getAPlusData,
  getProductExploreImages,
  getAllReviews,
  getAllReviewsV2,
  getAllImagesForReviews,
  getEcomPolicyData,
  getDeliveryAndShippingInfo,
} from 'shared/api/product-details';

// helpers
import { getDomain } from 'shared/helpers/app';

// store
import { ActionTypes } from './actions';
import { transformProductDetails, transformExploreData } from './adapter';

// redux actions
import { redirect } from 'store/app/actions';

// logging
import logger from 'shared/logger';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { log } from 'shared/helpers/newRelic';

const MODULE_IDENTRIFIER = 'PDP API';

// defs
interface IFetchPDPDataProps {
  productID: string;
  isNew: boolean;
  headers: { [key: string]: string };
  incomingURL: string;
  searchParams: string;
  skuId?: string;
  isMobile: boolean;
  isPriceReveal: boolean;
  isEddV2Enabled: boolean;
  isMeasurementChartV2: boolean;
}

interface IFetchPDPDataForSizeGuideProps {
  productID: string;
  isNew: boolean;
  headers: { [key: string]: string };
  searchParams: string;
  skuId?: string;
  isMobile: boolean;
  routeSku?: string;
  src?: string;
}

interface IFetchAllReviewsProps {
  productId: string;
  size: number;
  page: number;
  shouldFetchV2: boolean;
  productDesignCode: string;
}

interface IFetchExploreImagesProps {
  productID: string;
  maxImages: number;
  imageTagID: string;
}

export const fetchPDPData = ({
  productID,
  isNew,
  headers,
  incomingURL,
  searchParams,
  skuId,
  isMobile,
  isPriceReveal,
  isEddV2Enabled,
}: IFetchPDPDataProps): any => {
  return (dispatch: any) => {
    const headerWithDomain = { ...headers, domain: getDomain() };
    dispatch({ type: ActionTypes.FETCH_SKU_DATA_PROGRESS, payload: productID });
    const productDetails = getProductDetailData({
      productID,
      isNew,
      headers: headerWithDomain,
      isMobile,
      isPriceReveal,
      isEddV2Enabled,
    });

    return productDetails
      .then((pdpDetails) => {
        // correct URI from action_url without query params
        const correctURLWithoutParams = get(pdpDetails, 'response.product.action_url', '');
        // appending query params to make the correct URI
        const correctURLWithParams = correctURLWithoutParams.concat(searchParams);

        // checking if action_url is having a slug or not
        const validateUrl = /^\/.+\/p\/(\d)+/g.test(correctURLWithoutParams);

        if (validateUrl === false) {
          logger.error({
            ERROR: 'Slug Missing in action_url - PDP API',
            tags: { [TAG_IDENTIFIER.MODULE]: MODULE_IDENTRIFIER },
            extraInfo: pdpDetails,
          });
        }

        // checking the URIs without query params
        if (validateUrl && incomingURL != correctURLWithoutParams) {
          // sending the URL with query params
          return dispatch(redirect(correctURLWithParams, 301));
        }

        const { productData, selectedSKU } = transformProductDetails(pdpDetails.response, skuId);

        dispatch({
          type: ActionTypes.FETCH_SKU_DATA_SUCCESS,
          payload: {
            skuId,
            pdp: productData,
            selectedSKU,
          },
        });

        return pdpDetails;
      })
      .catch((err) => {
        if (err.status === 404) {
          dispatch({ type: ActionTypes.FETCH_SKU_DATA_NOT_FOUND, payload: productID });
        } else {
          dispatch({
            type: ActionTypes.FETCH_SKU_DATA_FAILED,
            payload: { productID, statusCode: err?.status },
          });
        }

        return Promise.reject(err);
      });
  };
};

export const fetchPDPDataForSizeGuide = ({
  productID,
  headers,
  skuId,
  isMobile,
  isNew = true,
  routeSku,
  src = '',
}: IFetchPDPDataForSizeGuideProps): any => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_SKU_DATA_PROGRESS, payload: productID });

    const productDetails = getProductDetailData({
      productID: productID,
      isNew,
      headers,
      isMobile,
      productSku: routeSku,
    });

    return productDetails
      .then((pdpDetails) => {
        const { productData, selectedSKU } = transformProductDetails(pdpDetails.response, skuId);

        dispatch({
          type: ActionTypes.FETCH_SKU_DATA_SUCCESS,
          payload: {
            skuId,
            pdp: productData,
            selectedSKU,
          },
        });

        return pdpDetails;
      })
      .catch((err) => {
        logger.error({
          ERROR: 'PDP API FETCH FOR SIZE GUIDE FAILED',
          MESSAGE: 'fetch pdp data for size guide failed',
          METHOD_NAME: 'fetchPDPDataForSizeGuide',
          INFO: {
            error: err,
            productID,
            skuId,
            isNew,
            src,
            isMobile,
          },
        });

        if (err.status === 404) {
          dispatch({ type: ActionTypes.FETCH_SKU_DATA_NOT_FOUND, payload: productID });
        } else {
          dispatch({
            type: ActionTypes.FETCH_SKU_DATA_FAILED,
            payload: { productID, statusCode: err?.status },
          });
        }

        return Promise.reject(err);
      });
  };
};

export const getAPlusContent = (productId: string) => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_A_PLUS_DATA_PROGRESS, payload: productId });
    return getAPlusData(productId)
      .then((data) => {
        const aplusContent = get(data, 'response.product.aplus_description', '');

        if (isEmpty(aplusContent)) {
          dispatch({
            type: ActionTypes.FETCH_A_PLUS_DATA_EMPTY_RESPONSE,
          });
          log({
            errTitle: 'Error in getAPlusContent API: APlus Content is empty',
            tags: { [TAG_IDENTIFIER.MODULE]: 'A_PLUS_CONTENT' },
          });
          return;
        }

        dispatch({
          type: ActionTypes.FETCH_A_PLUS_DATA_SUCCESS,
          payload: aplusContent,
        });
        return data;
      })
      .catch((error) => {
        dispatch({
          type: ActionTypes.FETCH_A_PLUS_DATA_FAILED,
          payload: { statusCode: error?.status, productId },
        });
      });
  };
};

export const fetchExploreImages = ({
  productID,
  maxImages,
  imageTagID,
}: IFetchExploreImagesProps) => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_EXPLORE_DATA_PROGRESS, payload: productID });
    return getProductExploreImages(productID, imageTagID)
      .then((response) => {
        const images = transformExploreData(response.data, maxImages);

        dispatch({
          type: ActionTypes.FETCH_EXPLORE_DATA_SUCCESS,
          payload: { images, productID },
        });
      })
      .catch((err) => {
        dispatch({ type: ActionTypes.FETCH_EXPLORE_DATA_FAILED, payload: productID });
        return Promise.reject(err);
      });
  };
};

export const fetchAllReviews = ({
  productId,
  size,
  page,
  shouldFetchV2,
  productDesignCode,
}: IFetchAllReviewsProps) => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_ALL_REVIEWS });

    const fetchAllReviewsListing = shouldFetchV2 ? getAllReviewsV2 : getAllReviews;

    return fetchAllReviewsListing({ productId, size, page, productDesignCode })
      .then((response: Review.IRootObject) => {
        dispatch({
          type: ActionTypes.UPDATE_ALL_REVIEWS_DATA,
          payload: {
            data: get(response, 'response.reviewData', []),
            page: get(response, 'response.page', 0),
          },
        });
      })
      .catch(() => {
        dispatch({
          type: ActionTypes.FETCH_ALL_REVIEWS_FAILED,
        });
      });
  };
};

export const fetchAllImagesFromCustomerReview = ({
  productId,
  pageKey,
  size = 20,
}: ReviewAllImages.IGetAllReviewImages) => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_ALL_IMAGES_FOR_REVIEWS });

    return getAllImagesForReviews({ productId, pageKey, size })
      .then((response: Review.IRootObject) => {
        const payload = get(response, 'response', {});
        const reviewImages = get(payload, 'reviewImages', []).map(
          (item: ReviewAllImages.IReviewImage) => ({
            id: String(item.reviewId),
            url: item.imageUrl,
          })
        );

        dispatch({
          type: ActionTypes.UPDATE_ALL_IMAGES_FOR_REVIEWS,
          payload: { ...payload, reviewImages },
        });
      })
      .catch(() => {
        dispatch({
          type: ActionTypes.FETCH_ALL_IMAGES_FOR_REVIEWS_FAILED,
        });
      });
  };
};

export const fetchEcomPolicyData = (productId: number, selectedItem: string) => {
  return (dispatch: any) => {
    dispatch({ type: ActionTypes.FETCH_ECOM_POLICY_DATA_PROGRESS, payload: productId });

    return getEcomPolicyData(productId, selectedItem)
      .then((response) => {
        dispatch({
          type: ActionTypes.FETCH_ECOM_POLICY_DATA_SUCCESS,
          payload: {
            selectedEcomPolicyData: get(response, `response.product.${selectedItem}`, ''),
            selectedEcomPolicy: selectedItem,
          },
        });
        return response;
      })
      .catch(() => {
        dispatch({ type: ActionTypes.FETCH_ECOM_POLICY_DATA_FAILED, payload: productId });
      });
  };
};

export const fetchDeliveryorShippedDate = (
  pincode: string,
  SKUs: string[],
  gatewayApiEnabled: boolean = true
) => {
  return (dispatch: any) => {
    return getDeliveryAndShippingInfo(pincode, SKUs, gatewayApiEnabled)
      .then((response) => {
        dispatch({
          type: ActionTypes.FETCH_DELIVERY_OR_SHIPPING_DATE_SUCCESS,
          payload: get(response, 'data.details', {}),
        });
        return response;
      })
      .catch(() => { });
  };
};
